/**
 * SignIn (Authentication) — login page rebuilt on composition primitives.
 *
 * Phone / Email mode toggle, two-field form, submit button with state-
 * dependent label, "Forgot password" ghost, footer with "Создать аккаунт"
 * and "adsensor.ru".
 *
 *   AuthShell
 *     └── CenteredCard (cardAs=form, noValidate)
 *           └── Stack gap="xl"
 *                 ├── LogoAssemble
 *                 └── Stack gap="m"
 *                       ├── SegmentedButton (mode)
 *                       ├── Stack (credential + password Inputs)
 *                       ├── ServerError (general, near button)
 *                       ├── submit Button
 *                       └── forgot Button
 *     └── FooterRow ("Создать аккаунт" + "adsensor.ru")
 *
 * Form state + validation are owned by react-hook-form (yup resolver).
 * Field errors are client-side (email format); server errors are opaque
 * backend messages surfaced in a single `serverError` slot near the submit
 * button — never attributed to a specific field. `mode` stays controlled by
 * the container; `initialValues`/`submitState`/callbacks unchanged.
 *
 * @format
 */

import {useEffect} from 'react';

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';
import {useTranslation} from 'react-i18next';
import {useForm, Controller} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as yup from 'yup';

import {LogoAssemble} from 'ui/components/molecules/LogoAssemble';
import {SegmentedButton, Segment} from 'ui/components/molecules/SegmentedButton';
import {Input} from 'ui/components/atoms/Input';
import {Button} from 'ui/components/atoms/Button';
import {Glow} from 'ui/components/atoms/Glow';
import {Stack} from 'ui/components/layout/Stack';
import {Row} from 'ui/components/layout/Row';
import {CenteredCard} from 'ui/components/composition/CenteredCard';
import {text} from 'ui/theme/typography';

import {AuthShell} from '../_shell';

export const SIGNIN_MODES = ['phone', 'email'] as const;
export type SignInMode = (typeof SIGNIN_MODES)[number];

export interface SignInValues {
  credential?: string;
  password?: string;
}

export interface SignInProps {
  /** Which credential the form is asking for. Controlled. Default `phone`. */
  mode?: SignInMode;
  /** Preseed the form. */
  initialValues?: SignInValues;
  /**
   * Opaque server error (wrong credentials, blocked account, …) shown near
   * the submit button. Not tied to a field — the backend returns machine
   * codes we can't reliably attribute to phone/email/password.
   */
  serverError?: string;
  /** Override submit state for Jobs rendering the mid-request frame. */
  submitState?: 'idle' | 'submitting';
  /** Mode toggle handler. */
  onModeChange?: (next: SignInMode) => void;
  /** Submit handler — fires only after client-side validation passes. */
  onSubmit?: (data: {mode: SignInMode; credential: string; password: string}) => void;
  /** Footer "Создать аккаунт" → navigate to /sign-up. */
  onSignUpClick?: () => void;
  /** "Не помню пароль" ghost button → navigate to /recovery. */
  onForgotPasswordClick?: () => void;
  /** Logo click → navigate to /. */
  onLogoClick?: () => void;
  /**
   * Second-level domain for the footer link + label (e.g. 'adsensor.ru' /
   * 'adsensor.io'). The container passes the build's domain so both the URL
   * and the visible text stay correct across .ru/.io.
   */
  adsensorDomain?: string;
}

// ── Per-mode copy / metadata ───────────────────────────────────────────

const FIELD_BY_MODE: Record<
  SignInMode,
  {
    type: 'tel' | 'email';
    placeholder: string;
    icon: string;
    autoComplete: string;
    submitEmptyLabel: string;
  }
> = {
  phone: {
    type: 'tel',
    placeholder: 'Телефон',
    icon: 'call',
    autoComplete: 'tel',
    submitEmptyLabel: 'Введите телефон и пароль',
  },
  email: {
    type: 'email',
    placeholder: 'Емейл',
    icon: 'mail',
    autoComplete: 'email',
    submitEmptyLabel: 'Введите емейл и пароль',
  },
};

// Footer needs the same 420 max-width as the card and a paddingTop = m so
// the gap to the card mirrors the original. A bare Row would have neither.
const FooterRow = styled(Row)(({theme}) => ({
  width: '100%',
  maxWidth: 420,
  paddingTop: theme.spacing(4),
  paddingLeft: theme.spacing(4),
  paddingRight: theme.spacing(4),
  boxSizing: 'border-box',
}));

// General server-error line, centered above the submit button.
const ServerError = styled(Box)(({theme}) => ({
  ...text('body.s'),
  color: theme.palette.bad.main,
  textAlign: 'center',
}));

// ── Component ──────────────────────────────────────────────────────────

export function SignIn({
  mode = 'phone',
  initialValues,
  serverError,
  submitState = 'idle',
  onModeChange,
  onSubmit,
  onSignUpClick,
  onForgotPasswordClick,
  onLogoClick,
  adsensorDomain = 'adsensor.ru',
}: SignInProps) {
  const {t} = useTranslation();

  // Email format is enforced only in email mode (via the `$mode` context);
  // emptiness is handled by the button's disabled state, so no `.required()`
  // here — that would surface yup's English default on blur.
  const schema = yup.object({
    credential: yup.string().when('$mode', {
      is: 'email',
      then: s => s.email(t('Введен некорректный email-адрес', 'Введен некорректный email-адрес')),
      otherwise: s => s,
    }),
    password: yup.string(),
  });

  const {control, handleSubmit, watch, formState, clearErrors} = useForm({
    defaultValues: {
      credential: initialValues?.credential ?? '',
      password: initialValues?.password ?? '',
    },
    resolver: yupResolver(schema),
    context: {mode},
    mode: 'onTouched',
  });

  // Switching phone↔email drops any stale email-format error on the shared
  // credential field until the user next interacts with it.
  useEffect(() => {
    clearErrors('credential');
  }, [mode, clearErrors]);

  const {credential, password} = watch();
  const fieldsFilled = Boolean(credential) && Boolean(password);
  const isSubmitting = submitState === 'submitting';
  const hasFieldError = Boolean(formState.errors.credential || formState.errors.password);
  const isValid = fieldsFilled && !isSubmitting;
  const showActionGlow = fieldsFilled && !hasFieldError && !serverError;

  const onValid = (values: {credential?: string; password?: string}) => {
    onSubmit?.({mode, credential: values.credential ?? '', password: values.password ?? ''});
  };

  const fieldMeta = FIELD_BY_MODE[mode];

  return (
    <AuthShell onLogoClick={onLogoClick}>
      <CenteredCard
        maxWidthPx={420}
        radius='xl'
        paddingX='m'
        paddingY='xl'
        gap='xl'
        contentAlignX='center'
        cardAs='form'
        cardProps={{onSubmit: handleSubmit(onValid), noValidate: true}}
        decoration={showActionGlow ? <Glow color='good' /> : null}
      >
        <LogoAssemble prefix='signIn' />

        <Stack gap='m' alignX='center'>
          <SegmentedButton
            mode='radio'
            value={mode}
            onChange={next => onModeChange?.(next as SignInMode)}
            size='s'
            disabled={isSubmitting}
          >
            <Segment value='phone' label={t('По телефону', 'По телефону')} />
            <Segment value='email' label={t('По емейлу', 'По емейлу')} />
          </SegmentedButton>

          <Stack gap='s'>
            <Controller
              name='credential'
              control={control}
              render={({field, fieldState}) => (
                <Input
                  {...field}
                  size='m'
                  type={fieldMeta.type}
                  fullWidth
                  leftIcon={fieldMeta.icon}
                  placeholder={t(fieldMeta.placeholder, fieldMeta.placeholder)}
                  autoComplete={fieldMeta.autoComplete}
                  errorMessage={fieldState.error?.message}
                  disabled={isSubmitting}
                />
              )}
            />
            <Controller
              name='password'
              control={control}
              render={({field, fieldState}) => (
                <Input
                  {...field}
                  size='m'
                  type='password'
                  fullWidth
                  placeholder={t('Пароль', 'Пароль')}
                  autoComplete='current-password'
                  errorMessage={fieldState.error?.message}
                  disabled={isSubmitting}
                />
              )}
            />
          </Stack>

          {serverError && <ServerError role='alert'>{serverError}</ServerError>}

          <Button
            type='submit'
            variant='solid'
            color='good'
            size='m'
            fullWidth
            disabled={!isValid}
            loading={isSubmitting ? 'left' : false}
          >
            {isSubmitting
              ? t('Входим в систему', 'Входим в систему')
              : fieldsFilled
                ? t('Войти', 'Войти')
                : t(fieldMeta.submitEmptyLabel, fieldMeta.submitEmptyLabel)}
          </Button>

          <Button
            type='button'
            variant='ghost'
            color='neutral'
            size='s'
            onClick={onForgotPasswordClick}
            disabled={isSubmitting}
          >
            {t('Не помню пароль', 'Не помню пароль')}
          </Button>
        </Stack>
      </CenteredCard>

      <FooterRow justify='between'>
        <Button variant='ghost' color='neutral' onClick={onSignUpClick} disabled={isSubmitting}>
          {t('Создать аккаунт', 'Создать аккаунт')}
        </Button>
        <Button
          variant='ghost'
          color='neutral'
          rightIcon='arrow_outward'
          onClick={() => window.open(`https://${adsensorDomain}`, '_blank', 'noopener')}
        >
          {adsensorDomain}
        </Button>
      </FooterRow>
    </AuthShell>
  );
}
